import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import ProgressMeter from "../components/ProgressMeter";
import ResmoMascot from "../components/ResmoMascot";
import { api, formatApiErrorDetail } from "../lib/api";
import { BookOpenCheck, Loader, Trash2, ArrowRight, Wand2 } from "lucide-react";

export default function PlaybooksList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedAnalysisId = (searchParams.get("analysis_id") || "").trim();
  const [list, setList] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    job_title: "",
    analysis_id: preselectedAnalysisId,
  });

  const refresh = async () => {
    try {
      const [pbRes, anRes] = await Promise.all([
        api.get("/playbooks"),
        api.get("/analyses"),
      ]);
      setList(pbRes.data || []);
      setAnalyses(anRes.data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const create = async () => {
    setError("");
    if (!form.analysis_id && !form.job_title && !form.company_name) {
      setError("Pick a saved analysis OR type a job title.");
      return;
    }
    setCreating(true);
    try {
      const body = {};
      if (form.analysis_id) body.analysis_id = form.analysis_id;
      if (form.company_name) body.company_name = form.company_name.trim();
      if (form.job_title) body.job_title = form.job_title.trim();
      const { data } = await api.post("/playbooks/generate", body);
      // Poll until the playbook is built, then navigate.
      const jobId = data.job_id;
      const until = Date.now() + 1000 * 60 * 2;
      while (Date.now() < until) {
        await new Promise((r) => setTimeout(r, 2500));
        try {
          const { data: status } = await api.get(`/jobs/${jobId}`);
          if (status.status === "complete" && status.playbook_id) {
            navigate(`/playbook/${status.playbook_id}`);
            return;
          }
          if (status.status === "error") {
            setError(status.error || "Generation failed.");
            setCreating(false);
            return;
          }
        } catch { /* keep polling */ }
      }
      setError("Resmo took too long. Try again.");
      setCreating(false);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setCreating(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this playbook? This can't be undone.")) return;
    try {
      await api.delete(`/playbooks/${id}`);
      refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="min-h-screen rm-paper pb-20">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
        <div className="rm-tag mb-3" data-testid="playbooks-tag">
          <BookOpenCheck size={12} /> Interview Playbook
        </div>
        <h1 className="font-display text-3xl md:text-5xl uppercase leading-tight mb-2">
          Your <span className="rm-text-orange">cheat sheets.</span>
        </h1>
        <p className="text-sm md:text-base mb-8 max-w-2xl" style={{ color: "#475569" }}>
          Each playbook is a printable, editable one-pager of likely questions paired with
          evidence bullets from your resume. Use it to prep before any interview — then walk
          in calm.
        </p>

        {/* Create form */}
        <div
          className="rm-card-static p-5 md:p-6 mb-8"
          style={{ background: "white" }}
          data-testid="playbook-create-form"
        >
          <div className="rm-label mb-3">Generate a new playbook</div>
          {analyses.length > 0 && (
            <div className="mb-3">
              <label className="rm-label">From a saved analysis</label>
              <select
                className="rm-input mt-1"
                value={form.analysis_id}
                onChange={(e) => setForm({ ...form, analysis_id: e.target.value })}
                data-testid="playbook-pick-analysis"
                disabled={creating}
              >
                <option value="">— or fill in manually below —</option>
                {analyses.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.job_title || "Untitled"}
                    {a.company_name ? ` @ ${a.company_name}` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="rm-label">Job title</label>
              <input
                type="text"
                className="rm-input mt-1"
                placeholder="e.g. Senior Backend Engineer"
                value={form.job_title}
                onChange={(e) => setForm({ ...form, job_title: e.target.value, analysis_id: "" })}
                data-testid="playbook-job-title"
                disabled={creating}
              />
            </div>
            <div>
              <label className="rm-label">Company (optional)</label>
              <input
                type="text"
                className="rm-input mt-1"
                placeholder="e.g. Acme Corp"
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value, analysis_id: "" })}
                data-testid="playbook-company"
                disabled={creating}
              />
            </div>
          </div>
          {error && (
            <div
              className="mt-3 p-3 rounded font-mono-rm text-sm"
              style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }}
              data-testid="playbook-error"
            >
              {error}
            </div>
          )}
          <button
            type="button"
            onClick={create}
            disabled={creating}
            className="rm-btn rm-btn-primary mt-4"
            data-testid="playbook-generate-btn"
          >
            {creating ? <Loader size={16} className="animate-spin" /> : <Wand2 size={16} />}
            {creating ? "Resmo is drafting..." : "Generate playbook (5 credits)"}
          </button>
          {creating && (
            <div className="mt-3 max-w-sm">
              <ProgressMeter expectedSeconds={30}
                             label="Picking questions + matching your resume evidence"
                             testid="playbook-progress-meter" />
            </div>
          )}
        </div>

        {/* Existing playbooks */}
        <div className="rm-label mb-2">Your playbooks</div>
        {list === null ? (
          <div className="text-center py-10 font-mono-rm text-sm" style={{ color: "#475569" }}>
            Loading...
          </div>
        ) : list.length === 0 ? (
          <div
            className="rm-card-static p-8 text-center"
            data-testid="playbooks-empty"
            style={{ background: "white" }}
          >
            <ResmoMascot state="hungry" size={120} />
            <div className="font-display text-xl uppercase mt-3 mb-1">No playbooks yet</div>
            <p className="text-sm" style={{ color: "#475569" }}>
              Generate your first playbook above. Resmo will draft 8-12 likely questions and pair
              each one with evidence from your resume.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="playbooks-grid">
            {list.map((pb) => (
              <div
                key={pb.id}
                className="rm-card-static p-5"
                style={{ background: "white" }}
                data-testid={`playbook-card-${pb.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="font-display text-lg uppercase leading-tight">
                    {pb.title || "Untitled playbook"}
                  </div>
                  <button
                    type="button"
                    onClick={() => onDelete(pb.id)}
                    className="rm-btn rm-btn-ghost text-xs px-2 py-1"
                    title="Delete this playbook"
                    data-testid={`playbook-delete-${pb.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
                {(pb.company_name || pb.job_title) && (
                  <div
                    className="font-mono-rm text-xs uppercase tracking-widest mb-3"
                    style={{ color: "#475569" }}
                  >
                    {pb.job_title || ""}
                    {pb.company_name ? ` @ ${pb.company_name}` : ""}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => navigate(`/playbook/${pb.id}`)}
                  className="rm-btn rm-btn-primary text-xs px-3 py-2"
                  data-testid={`playbook-open-${pb.id}`}
                >
                  Open <ArrowRight size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 text-center">
          <button
            onClick={() => navigate("/mock-interview")}
            className="rm-btn rm-btn-ghost text-xs"
            data-testid="back-to-mock-btn"
          >
            ← Back to Mock Interview
          </button>
        </div>
      </div>
    </div>
  );
}
